import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { copyToClipboard } from "../../lib/clipboard";
import { toast } from "sonner";
import { Link as LinkIcon, Copy, ShareNetwork, UserPlus, CheckCircle, Eye, Trash, X } from "@phosphor-icons/react";
import { QRCodeSVG } from "qrcode.react";

export default function MyAffiliate() {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [newSlug, setNewSlug] = useState("");
  const [leads, setLeads] = useState([]);
  const [approving, setApproving] = useState("");
  const [viewLead, setViewLead] = useState(null);
  const [deleteLead, setDeleteLead] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [tab, setTab] = useState("active");
  const [excluded, setExcluded] = useState([]);
  const [restoring, setRestoring] = useState("");

  const load = async () => {
    const [a, l] = await Promise.all([api.get("/affiliates/me"), api.get("/affiliates/leads")]);
    setData(a.data); setLeads(l.data);
  };
  const loadExcluded = async () => {
    const { data: x } = await api.get("/affiliates/leads/excluded");
    setExcluded(x);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (tab === "trash") loadExcluded(); }, [tab]);

  const fullUrl = data ? `${window.location.origin}/com/${data.slug}` : "";

  const copy = async () => {
    const ok = await copyToClipboard(fullUrl);
    ok ? toast.success("Link copiado!") : toast.error("Falha ao copiar");
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Cris Lingerie", text: "Cadastre-se com meu link da Cris Lingerie", url: fullUrl });
      } catch {}
    } else { copy(); }
  };

  const saveSlug = async () => {
    try {
      const { data: r } = await api.post("/affiliates/me/slug", { slug: newSlug });
      toast.success(`Slug atualizado para "${r.slug}"`);
      setEditing(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const approveLead = async (id) => {
    setApproving(id);
    try {
      const { data: r } = await api.post(`/affiliates/leads/${id}/approve`);
      toast.success(r.message || "Lead aprovado");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao aprovar"); }
    finally { setApproving(""); }
  };

  const confirmDelete = async () => {
    if (!deleteLead) return;
    setDeleting(true);
    try {
      const { data: r } = await api.delete(`/affiliates/leads/${deleteLead.id}`);
      toast.success(r.message || "Lead excluido");
      setDeleteLead(null);
      load();
      if (tab === "trash") loadExcluded();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao excluir"); }
    finally { setDeleting(false); }
  };

  const restoreLead = async (id) => {
    setRestoring(id);
    try {
      const { data: r } = await api.post(`/affiliates/leads/${id}/restore`);
      toast.success(r.message || "Lead restaurado");
      loadExcluded();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao restaurar"); }
    finally { setRestoring(""); }
  };

  if (!data) return <div className="text-sm font-medium uppercase tracking-widest text-brand">Carregando...</div>;

  return (
    <div data-testid="my-affiliate-page" className="space-y-6">
      <div className="topbar">
        <div>
          <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-brand">Link de indicação</div>
          <h1 className="mt-1 text-3xl font-bold tracking-tight text-brand-dark">Meu Link</h1>
        </div>
      </div>

      {/* Hero: QR + link + stats */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* QR card */}
        <div className="flex flex-col items-center justify-center rounded-3xl bg-brand-grad p-6 text-center text-white shadow-pop">
          <div className="rounded-2xl bg-white p-3 shadow-lg" data-testid="affiliate-qr">
            {fullUrl && <QRCodeSVG value={fullUrl} size={148} fgColor="#2B0D2E" bgColor="#FFFFFF" level="M" />}
          </div>
          <div className="mt-4 text-sm font-medium text-white/85">Aponte a câmera para se cadastrar</div>
        </div>

        {/* Link + actions */}
        <div className="rounded-3xl border border-border bg-white p-6 shadow-card lg:col-span-2">
          <div className="mb-3 flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.12em] text-brand">
            <LinkIcon size={18} weight="duotone" /> Seu link exclusivo
          </div>
          {!editing ? (
            <>
              <div className="mb-5 break-all rounded-2xl bg-muted/60 px-4 py-3 text-lg font-semibold tabular text-brand-dark md:text-xl" data-testid="affiliate-link">{fullUrl}</div>
              <div className="flex flex-wrap gap-3">
                <button onClick={copy} className="brand" data-testid="copy-affiliate-btn"><Copy size={16} weight="bold" /> Copiar</button>
                <button onClick={share} className="brand-outline" data-testid="share-affiliate-btn"><ShareNetwork size={16} weight="bold" /> Compartilhar</button>
                <button onClick={() => { setEditing(true); setNewSlug(data.slug); }} className="text-xs font-semibold uppercase tracking-widest text-brand hover:underline" data-testid="edit-slug-btn">Personalizar</button>
              </div>
              <div className="mt-6 grid grid-cols-2 gap-3">
                <div className="rounded-2xl border border-border bg-white p-4">
                  <div className="text-[10px] uppercase tracking-wide text-[#9A8A98]">Indicações</div>
                  <div className="mt-1 text-2xl font-bold tabular text-brand-dark" data-testid="stat-referrals">{data.referrals_count}</div>
                </div>
                <div className="rounded-2xl border border-border bg-white p-4">
                  <div className="text-[10px] uppercase tracking-wide text-[#9A8A98]">Vendas geradas</div>
                  <div className="mt-1 text-2xl font-bold tabular text-brand-pink" data-testid="stat-sales">{data.sales_count}</div>
                </div>
              </div>
            </>
          ) : (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2 text-lg">
                <span className="text-[#7A6A78]">{window.location.origin}/com/</span>
                <input value={newSlug} onChange={(e) => setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))} className="input-underline tabular flex-1" placeholder="seu-nome" data-testid="slug-input" />
              </div>
              <div className="flex gap-3">
                <button onClick={saveSlug} className="brand" data-testid="save-slug-btn">Salvar</button>
                <button onClick={() => setEditing(false)} className="text-xs font-semibold uppercase tracking-widest text-[#7A6A78] hover:underline">Cancelar</button>
              </div>
            </div>
          )}
        </div>
      </div>


      <div className="flex items-center gap-6 border-b border-border mb-4">
        <button onClick={() => setTab("active")} className={`pb-2 text-xs tracking-widest uppercase font-semibold flex items-center gap-2 border-b-2 -mb-px ${tab === "active" ? "border-brand text-brand" : "border-transparent text-[#7A6A78] hover:text-brand"}`} data-testid="tab-active-leads">
          <UserPlus size={16} weight="duotone" /> Leads recebidos ({leads.length})
        </button>
        <button onClick={() => setTab("trash")} className={`pb-2 text-xs tracking-widest uppercase font-medium flex items-center gap-2 border-b-2 -mb-px ${tab === "trash" ? "border-[#B4564F] text-[#B4564F]" : "border-transparent text-[#5C5A57] hover:text-[#B4564F]"}`} data-testid="tab-trash-leads">
          <Trash size={16} weight="duotone" /> Excluidos
        </button>
      </div>

      {tab === "active" ? (
      <div className="table-wrap mb-8" data-testid="active-leads-table">
        <table className="crl">
          <thead><tr><th>Recebido</th><th>Nome</th><th>CPF</th><th>WhatsApp</th><th>Cidade</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {leads.map((l) => (
              <tr key={l.id}>
                <td className="text-[#5C5A57] text-xs">{new Date(l.created_at).toLocaleDateString("pt-BR")}</td>
                <td className="font-medium">{l.name}</td>
                <td className="text-[#5C5A57] text-xs tabular">{l.cpf || "-"}</td>
                <td className="text-[#5C5A57] text-xs">{l.phone}</td>
                <td className="text-[#5C5A57] text-xs">{l.city || "-"}</td>
                <td><span className={`badge ${l.status === "approved" ? "green" : "medio"}`}>{l.status === "approved" ? "aprovado" : "novo"}</span></td>
                <td>
                  <div className="flex items-center gap-3 justify-end">
                    <button onClick={() => setViewLead(l)} className="text-xs uppercase tracking-widest font-medium hover:underline flex items-center gap-1" data-testid={`view-lead-${l.id}`}>
                      <Eye size={14} /> Visualizar
                    </button>
                    {l.status !== "approved" ? (
                      <button onClick={() => approveLead(l.id)} disabled={approving === l.id} className="text-xs uppercase tracking-widest font-medium hover:underline flex items-center gap-1 disabled:opacity-40" data-testid={`approve-lead-${l.id}`}>
                        <CheckCircle size={14} /> {approving === l.id ? "Aprovando..." : "Aprovar"}
                      </button>
                    ) : (
                      <span className="text-[10px] uppercase tracking-widest text-[#7B9D7E]">Cliente criada</span>
                    )}
                    <button onClick={() => setDeleteLead(l)} className="text-xs uppercase tracking-widest font-medium text-[#B4564F] hover:underline flex items-center gap-1" data-testid={`delete-lead-${l.id}`}>
                      <Trash size={14} /> Excluir
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {leads.length === 0 && <tr><td colSpan="7" className="text-center text-sm text-[#5C5A57] py-8">Compartilhe seu link e os leads aparecem aqui.</td></tr>}
          </tbody>
        </table>
      </div>
      ) : (
      <div className="table-wrap mb-8" data-testid="trash-leads-table">
        <table className="crl">
          <thead><tr><th>Nome</th><th>CPF</th><th>WhatsApp</th><th>Excluido em</th><th>Excluido por</th><th></th></tr></thead>
          <tbody>
            {excluded.map((l) => (
              <tr key={l.id}>
                <td className="font-medium">{l.name}</td>
                <td className="text-[#5C5A57] text-xs tabular">{l.cpf || "-"}</td>
                <td className="text-[#5C5A57] text-xs">{l.phone}</td>
                <td className="text-[#5C5A57] text-xs">{l.deleted_at ? new Date(l.deleted_at).toLocaleString("pt-BR") : "-"}</td>
                <td className="text-[#5C5A57] text-xs">{l.deleted_by_name || "-"}</td>
                <td>
                  <div className="flex items-center gap-3 justify-end">
                    <button onClick={() => setViewLead(l)} className="text-xs uppercase tracking-widest font-medium hover:underline flex items-center gap-1" data-testid={`view-excluded-${l.id}`}>
                      <Eye size={14} /> Visualizar
                    </button>
                    <button onClick={() => restoreLead(l.id)} disabled={restoring === l.id} className="text-xs uppercase tracking-widest font-medium text-[#7B9D7E] hover:underline flex items-center gap-1 disabled:opacity-40" data-testid={`restore-lead-${l.id}`}>
                      <CheckCircle size={14} /> {restoring === l.id ? "Restaurando..." : "Restaurar"}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {excluded.length === 0 && <tr><td colSpan="6" className="text-center text-sm text-[#5C5A57] py-8">Nenhum lead excluido.</td></tr>}
          </tbody>
        </table>
      </div>
      )}

      <h2 className="section-title mb-4">Minhas indicacoes</h2>
      <div className="table-wrap">
        <table className="crl">
          <thead><tr><th>Cliente</th><th>Status</th><th>Score</th><th>Cadastrada</th></tr></thead>
          <tbody>
            {data.referrals.map((c) => (
              <tr key={c.id}>
                <td className="font-medium">{c.name}</td>
                <td className="text-[#5C5A57] text-xs">{c.status}</td>
                <td className="tabular">{c.score}</td>
                <td className="text-[#5C5A57] text-xs">{new Date(c.created_at).toLocaleDateString("pt-BR")}</td>
              </tr>
            ))}
            {data.referrals.length === 0 && <tr><td colSpan="4" className="text-center text-sm text-[#5C5A57] py-8">Ainda sem indicacoes convertidas.</td></tr>}
          </tbody>
        </table>
      </div>

      {viewLead && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-6" onClick={() => setViewLead(null)}>
          <div className="bg-white border border-[#EBE5DF] max-w-lg w-full p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="view-lead-modal">
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C5A57]">Pre-cadastro</div>
                <h2 className="font-display text-2xl font-bold tracking-tight mt-1">{viewLead.name}</h2>
              </div>
              <button onClick={() => setViewLead(null)} className="text-[#5C5A57] hover:text-black" data-testid="close-view-lead-btn"><X size={20} /></button>
            </div>

            <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C5A57] border-b border-[#EBE5DF] pb-1 mb-3">Dados pessoais</div>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm mb-6">
              <div><dt className="text-[10px] tracking-widest uppercase text-[#5C5A57]">Nome completo</dt><dd className="font-medium">{viewLead.name || "-"}</dd></div>
              <div><dt className="text-[10px] tracking-widest uppercase text-[#5C5A57]">CPF</dt><dd className="tabular">{viewLead.cpf || "-"}</dd></div>
              <div><dt className="text-[10px] tracking-widest uppercase text-[#5C5A57]">RG</dt><dd>{viewLead.rg || "-"}</dd></div>
              <div><dt className="text-[10px] tracking-widest uppercase text-[#5C5A57]">Data de nascimento</dt><dd>{viewLead.birth_date || "-"}</dd></div>
              <div><dt className="text-[10px] tracking-widest uppercase text-[#5C5A57]">WhatsApp</dt><dd>{viewLead.phone || "-"}</dd></div>
              <div><dt className="text-[10px] tracking-widest uppercase text-[#5C5A57]">E-mail</dt><dd className="break-all">{viewLead.email || "-"}</dd></div>
            </dl>

            <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C5A57] border-b border-[#EBE5DF] pb-1 mb-3">Endereco</div>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm mb-6">
              <div><dt className="text-[10px] tracking-widest uppercase text-[#5C5A57]">CEP</dt><dd>{viewLead.address?.cep || "-"}</dd></div>
              <div><dt className="text-[10px] tracking-widest uppercase text-[#5C5A57]">Rua</dt><dd>{viewLead.address?.street || "-"}</dd></div>
              <div><dt className="text-[10px] tracking-widest uppercase text-[#5C5A57]">Numero</dt><dd>{viewLead.address?.number || "-"}</dd></div>
              <div><dt className="text-[10px] tracking-widest uppercase text-[#5C5A57]">Complemento</dt><dd>{viewLead.address?.complement || "-"}</dd></div>
              <div><dt className="text-[10px] tracking-widest uppercase text-[#5C5A57]">Bairro</dt><dd>{viewLead.address?.district || "-"}</dd></div>
              <div><dt className="text-[10px] tracking-widest uppercase text-[#5C5A57]">Cidade</dt><dd>{viewLead.address?.city || "-"}</dd></div>
              <div><dt className="text-[10px] tracking-widest uppercase text-[#5C5A57]">UF</dt><dd>{viewLead.address?.state || "-"}</dd></div>
            </dl>

            <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C5A57] border-b border-[#EBE5DF] pb-1 mb-3">Dados da indicacao</div>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm mb-6">
              <div><dt className="text-[10px] tracking-widest uppercase text-[#5C5A57]">Data do cadastro</dt><dd>{viewLead.created_at ? new Date(viewLead.created_at).toLocaleString("pt-BR") : "-"}</dd></div>
              <div><dt className="text-[10px] tracking-widest uppercase text-[#5C5A57]">Revendedora responsavel</dt><dd>{viewLead.reseller_name || "-"}</dd></div>
              <div className="col-span-2"><dt className="text-[10px] tracking-widest uppercase text-[#5C5A57]">Link utilizado</dt><dd className="break-all">{viewLead.affiliate_slug ? `${window.location.origin}/com/${viewLead.affiliate_slug}` : "-"}</dd></div>
              <div><dt className="text-[10px] tracking-widest uppercase text-[#5C5A57]">Status do lead</dt><dd><span className={`badge ${viewLead.status === "approved" ? "green" : viewLead.status === "excluded" ? "risco" : "medio"}`}>{viewLead.status === "approved" ? "aprovado" : viewLead.status === "excluded" ? "excluido" : "novo"}</span></dd></div>
            </dl>

            {viewLead.message && (
              <div className="mb-6">
                <div className="text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Mensagem</div>
                <p className="text-sm text-[#5C5A57]">{viewLead.message}</p>
              </div>
            )}

            <div className="flex flex-wrap gap-3 pt-4 border-t border-[#EBE5DF]">
              {viewLead.status === "excluded" ? (
                <button onClick={() => { const id = viewLead.id; setViewLead(null); restoreLead(id); }} className="brand" style={{ background: "#7B9D7E" }} data-testid="view-modal-restore-btn"><CheckCircle size={14} className="inline mr-2" />Restaurar</button>
              ) : (
                <>
                  {viewLead.status !== "approved" && (
                    <button onClick={() => { const id = viewLead.id; setViewLead(null); approveLead(id); }} className="brand" data-testid="view-modal-approve-btn"><CheckCircle size={14} className="inline mr-2" />Aprovar</button>
                  )}
                  <button onClick={() => { setDeleteLead(viewLead); setViewLead(null); }} className="brand" style={{ background: "#B4564F" }} data-testid="view-modal-delete-btn"><Trash size={14} className="inline mr-2" />Excluir</button>
                </>
              )}
              <button onClick={() => setViewLead(null)} className="text-xs tracking-widest uppercase text-[#5C5A57] hover:underline">Fechar</button>
            </div>
          </div>
        </div>
      )}

      {deleteLead && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-6" onClick={() => setDeleteLead(null)}>
          <div className="bg-white border border-[#EBE5DF] max-w-md w-full p-8" onClick={(e) => e.stopPropagation()} data-testid="delete-lead-modal">
            <h2 className="font-display text-2xl font-bold tracking-tight mb-3">Excluir lead</h2>
            <p className="text-sm text-[#5C5A57] mb-6">Tem certeza que deseja excluir este lead? Esta acao nao podera ser desfeita.</p>
            <div className="flex gap-3">
              <button onClick={confirmDelete} disabled={deleting} className="brand" style={{ background: "#B4564F" }} data-testid="confirm-delete-lead-btn">{deleting ? "Excluindo..." : "Excluir"}</button>
              <button onClick={() => setDeleteLead(null)} className="text-xs tracking-widest uppercase text-[#5C5A57] hover:underline" data-testid="cancel-delete-lead-btn">Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
